import {
    ChangePercentage,
    CountryCell,
    DefaultCellRightAlign,
    GroupTrafficShare,
    IndexCell,
    RankCell,
    TrafficShare,
} from "app/components/React/Table/cells";
import type { GroupTrafficShareProps } from "app/components/React/Table/cells/GroupTrafficShare";
import { PercentageCellRightAlign } from "app/components/React/Table/cells/PercentageCell";
import type { IRegionalGroupTrafficShareProps } from "app/components/React/Table/cells/RegionalGroupTrafficShare";
import { RegionalGroupTrafficShare } from "app/components/React/Table/cells/RegionalGroupTrafficShare";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { i18nFilter } from "app/filters/i18nFilter";

const GeographyTableColumnsSingleConfig = [
    {
        fixed: true,
        cellComponent: IndexCell,
        headerComponent: DefaultCellHeader,
        disableHeaderCellHover: true,
        sortable: false,
        width: 65,
        visible: true,
    },
    {
        fixed: true,
        field: "Country",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.country.title");
        },
        type: "string",
        format: "None",
        sortable: true,
        isSorted: false,
        cellComponent: CountryCell,
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        totalCount: true,
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.country.title.tooltip");
        },
        width: 240,
        showTotalCount: true,
        visible: true,
        ppt: {
            // override the table column format when rendered in ppt
            overrideFormat: "Country",
        },
    },
    {
        field: "Share",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.share.title");
        },
        type: "double",
        sortable: true,
        isSorted: false,
        cellComponent: TrafficShare,
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.share.title.tooltip");
        },
        width: 130,
        visible: true,
    },
    {
        field: "Change",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.change.title");
        },
        type: "double",
        format: "percentagesign",
        sortable: true,
        isSorted: false,
        cellComponent: ChangePercentage,
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("widget.table.tooltip.topsites.change");
        },
        width: 130,
        visible: true,
    },
    {
        field: "Rank",
        get displayName() {
            return i18nFilter()("wa.ao.ranks.country");
        },
        type: "long",
        format: "swRank",
        sortable: true,
        isSorted: false,
        sortDirection: "desc",
        groupable: false,
        cellComponent: RankCell,
        headerComponent: DefaultCellHeaderRightAlign,
        totalCount: false,
        get tooltip() {
            return i18nFilter()("widget.table.tooltip.topsites.rank");
        },
        width: 130,
        visible: true,
    },
    {
        field: "AvgVisitDuration",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.AvgTime.title");
        },
        type: "double",
        format: "time",
        sortable: true,
        isSorted: false,
        cellComponent: DefaultCellRightAlign,
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("widget.table.tooltip.topsites.avgvisitduration");
        },
        width: 130,
        visible: true,
    },
    {
        field: "PagePerVisit",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.PPV.title");
        },
        type: "double",
        format: "number:2",
        sortable: true,
        isSorted: false,
        cellComponent: DefaultCellRightAlign,
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("widget.table.tooltip.topsites.ppv");
        },
        width: 130,
        visible: true,
    },
    {
        field: "BounceRate",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.BounceRate.title");
        },
        type: "double",
        format: "percentage:2",
        sortable: true,
        isSorted: false,
        cellComponent: PercentageCellRightAlign,
        headerComponent: DefaultCellHeaderRightAlign,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("widget.table.tooltip.topsites.bouncerate");
        },
        width: 130,
        visible: true,
    },
];

const GeographyTableColumnsCompareConfigBase = [
    {
        fixed: true,
        cellComponent: IndexCell,
        headerComponent: DefaultCellHeader,
        disableHeaderCellHover: true,
        sortable: false,
        width: 65,
        visible: true,
    },
    {
        fixed: true,
        field: "Country",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.country.title");
        },
        type: "string",
        format: "None",
        sortable: true,
        isSorted: false,
        cellComponent: CountryCell,
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        totalCount: true,
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.country.title.tooltip");
        },
        width: 240,
        showTotalCount: true,
        visible: true,
        ppt: {
            // override the table column format when rendered in ppt
            overrideFormat: "Country",
        },
    },
    {
        field: "Share",
        get displayName() {
            return i18nFilter()("analysis.audience.geo.table.columns.share.title");
        },
        type: "double",
        sortable: true,
        isSorted: false,
        cellComponent: TrafficShare,
        headerComponent: DefaultCellHeader,
        sortDirection: "desc",
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.share.title.tooltip");
        },
        width: 130,
        visible: true,
    },
];

const GeographyTableColumnsCompareConfigRegionals = [
    {
        field: "EnrichedShareList",
        get displayName() {
            return i18nFilter()("analysis.source.search.all.table.columns.shareCompare.title");
        },
        type: "double[]",
        format: "percentagesign",
        sortable: false,
        isSorted: false,
        groupable: false,
        headerComponent: DefaultCellHeader,
        cellComponent: RegionalGroupTrafficShare,
        totalCount: false,
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.shareCompare.title.tooltip");
        },
        width: 360,
        visible: true,
        ppt: {
            // Indicates that we want to split this column into sub-columns
            // where each sub-column will be a table entity (table item - a website/app/category.
            // such as ynet.co.il, whatsapp, all industries etc.)
            splitColumnToEntities: true,

            // override the table column format when rendered in ppt
            overrideFormat: "smallNumbersPercentage:1",

            // Override default value when no value is present in a table row
            defaultValue: "0",
        },
    },
];
const GeographyTableColumnsCompareConfigDefault = [
    {
        field: "ShareList",
        get displayName() {
            return i18nFilter()("analysis.source.search.all.table.columns.shareCompare.title");
        },
        type: "double[]",
        format: "percentagesign",
        sortable: false,
        isSorted: false,
        groupable: false,
        headerComponent: DefaultCellHeader,
        cellComponent: GroupTrafficShare,
        totalCount: false,
        get tooltip() {
            return i18nFilter()("analysis.audience.geo.table.columns.shareCompare.title.tooltip");
        },
        width: 360,
        visible: true,
        ppt: {
            // Indicates that we want to split this column into sub-columns
            // where each sub-column will be a table entity (table item - a website/app/category.
            // such as ynet.co.il, whatsapp, all industries etc.)
            splitColumnToEntities: true,

            // override the table column format when rendered in ppt
            overrideFormat: "smallNumbersPercentage:1",

            // Override default value when no value is present in a table row
            defaultValue: "0",
        },
    },
];

const UsersShareColumnConfig = {
    field: "UsersShare",
    get displayName() {
        return i18nFilter()("analysis.audience.geo.table.columns.users.share.title");
    },
    type: "double",
    sortable: true,
    isSorted: false,
    cellComponent: TrafficShare,
    headerComponent: DefaultCellHeader,
    sortDirection: "desc",
    get tooltip() {
        return i18nFilter()("analysis.audience.geo.table.columns.users.share.title.tooltip");
    },
    width: 130,
    visible: true,
};

const UsersShareListColumnConfig = {
    field: "UsersShareList",
    get displayName() {
        return i18nFilter()("analysis.audience.geo.table.columns.users.shareCompare.title");
    },
    type: "double[]",
    format: "percentagesign",
    sortable: false,
    isSorted: false,
    groupable: false,
    headerComponent: DefaultCellHeader,
    cellComponent: (props: GroupTrafficShareProps) => {
        return GroupTrafficShare({
            ...props,
            tooltipKey: "analysis.audience.geo.table.columns.users.shareCompare.tooltip",
        });
    },
    totalCount: false,
    get tooltip() {
        return i18nFilter()("analysis.audience.geo.table.columns.users.shareCompare.tooltip");
    },
    width: 360,
    visible: true,
};

const getUsersShareListWithRegionalsColumnConfig = () => ({
    ...UsersShareListColumnConfig,
    field: "EnrichedUsersShareList",
    cellComponent: (props: IRegionalGroupTrafficShareProps) => {
        return RegionalGroupTrafficShare({
            ...props,
            tooltipKey: "analysis.audience.geo.table.columns.users.shareCompare.tooltip",
        });
    },
});

const getGeographyCompareConfig = (isRegionalsEnabled, sortbyField, sortDirection, isLast28Days) => {
    const tableColumnUsersShare = isLast28Days
        ? []
        : [
              UsersShareColumnConfig,
              isRegionalsEnabled ? getUsersShareListWithRegionalsColumnConfig() : UsersShareListColumnConfig,
          ];
    const tableColumns = isRegionalsEnabled
        ? [
              ...GeographyTableColumnsCompareConfigBase,
              ...GeographyTableColumnsCompareConfigRegionals,
              ...tableColumnUsersShare,
          ]
        : [
              ...GeographyTableColumnsCompareConfigBase,
              ...GeographyTableColumnsCompareConfigDefault,
              ...tableColumnUsersShare,
          ];
    return tableColumns.map((col: any, idx) => {
        if (!col.sortable) {
            return col;
        }
        return {
            ...col,
            isSorted: col.field === sortbyField,
            sortDirection: col.field === sortbyField ? sortDirection : col.sortDirection,
        };
    });
};

export const geographyTableColumnsConfigGen = (
    sortbyField: string,
    sortDirection: string,
    isCompare: boolean,
    isRegionalsEnabled: boolean,
    isLast28Days: boolean = false,
) => {
    if (isCompare) {
        return getGeographyCompareConfig(isRegionalsEnabled, sortbyField, sortDirection, isLast28Days);
    }
    const GeographyTableColumnsSingleColumns = [...GeographyTableColumnsSingleConfig];
    if (!isLast28Days) {
        GeographyTableColumnsSingleColumns.splice(
            GeographyTableColumnsSingleColumns.findIndex((item) => item.field === "Rank"),
            0,
            UsersShareColumnConfig,
        );
    }
    return GeographyTableColumnsSingleColumns.map((col, idx) => {
        if (!col.sortable) {
            return col;
        }
        return {
            ...col,
            isSorted: col.field === sortbyField,
            sortDirection: col.field === sortbyField ? sortDirection : col.sortDirection,
        };
    });
};
